import * as React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import type { FeedbackResponseCreateJson } from "@volley/data";
import { UserSession } from "@volley/shared/coach-models";

import logger, { useLogMountTime } from "../../log";
import { fetchApi } from "../../util";
import CloseableDialogTitle from "../common/CloseableDialogTitle";
import FiveStarRating from "../common/FiveStarRating";
import { usePairingContext } from "../hooks/pairingStatus";

import { DialogType } from "./DialogContext";
import useDialog from "./useDialog";

export const LOCAL_STORAGE_KEY = "SESSION_FEEDBACK_LAST_DATE_NEW_HOMEPAGE";
export const SESSION_LENGTH_MINUTES = 5;
const FEEDBACK_COOLDOWN_DAYS = 14;
const FEEDBACK_COOLDOWN_MS = FEEDBACK_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

export function shouldSolicitFeedback(
    session: UserSession | null | undefined,
    now = Date.now(),
): boolean {
    try {
        // if we don't have a previous date or the session, we should solicit feedback
        if (!session) {
            logger.warn("[feedback] No session, soliciting feedback");
            return true;
        }

        // session length check, must meet min length
        const sessionStart = new Date(session.startTimestamp);
        const sessionLengthRequired = SESSION_LENGTH_MINUTES * 60 * 1000;

        if (Number.isNaN(sessionStart.getTime())) {
            logger.error(
                "[feedback] Error parsing session start time",
                session.startTimestamp,
            );
            return false;
        }

        const sessionDuration = now - sessionStart.getTime();
        if (sessionDuration < sessionLengthRequired) {
            logger.info(
                `[feedback] Session length is ${sessionDuration}, less than ${sessionLengthRequired}`,
            );
            return false;
        }

        // if we don't have a previous date, we should solicit feedback
        const lastDate = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (!lastDate) {
            logger.info(
                "[feedback] No last solicited date, soliciting feedback",
            );
            return true;
        }

        // if lastDate is not a number, we should solicit feedback
        // and reset localStorage value
        const lastDateNum = parseInt(lastDate, 10);
        if (Number.isNaN(lastDateNum)) {
            logger.warn(
                "[feedback] Last solicited date is not a number, resetting localStorage",
            );
            localStorage.removeItem(LOCAL_STORAGE_KEY);
            return true;
        }

        // Check if enough time has elapsed since last feedback
        const elapsed = now - lastDateNum;
        if (elapsed >= FEEDBACK_COOLDOWN_MS) {
            logger.info(
                `[feedback] ${Math.floor(elapsed / (24 * 60 * 60 * 1000))} days since last feedback, soliciting feedback`,
            );
            return true;
        }

        logger.info(
            `[feedback] Only ${Math.floor(elapsed / (24 * 60 * 60 * 1000))} days since last feedback, waiting for ${FEEDBACK_COOLDOWN_DAYS} day cooldown`,
        );
    } catch (e) {
        logger.error(
            "[feedback] Error checking if we should solicit feedback",
            e as Error,
        );
    }

    return false;
}

interface FeedbackDialogProps {
    postDialog: DialogType;
}
function FeedbackDialog({
    postDialog,
}: FeedbackDialogProps): React.JSX.Element {
    useLogMountTime("FeedbackDialog");
    const { sessionId } = usePairingContext();
    const { setDialogType } = useDialog();
    const [rating, setRating] = React.useState<number | null>(null);
    const [comment, setComments] = React.useState<string>("");
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [apiError, setApiError] = React.useState<string>("");

    React.useEffect(() => {
        if (sessionId) {
            localStorage.setItem(LOCAL_STORAGE_KEY, Date.now().toString());
        }
    }, [sessionId]);

    const handleSave = async () => {
        try {
            if (!sessionId) {
                throw new Error("Session ID is undefined");
            }

            if (!rating) {
                throw new Error("Rating is undefined");
            }

            setSubmitting(true);
            setApiError(""); // Clear any previous API errors

            const feedbackResponse: FeedbackResponseCreateJson = {
                rating,
                comment: comment || null,
                session: { id: sessionId },
            };

            try {
                await fetchApi("/api/feedback", "POST", feedbackResponse);
                setDialogType(postDialog);
            } catch (apiErr) {
                logger.error("Error submitting feedback", apiErr as Error);
                setApiError("Failed to submit feedback. Please try again.");
            }
        } catch (e) {
            logger.error("Error in feedback submission", e as Error);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <CloseableDialogTitle onClose={() => setDialogType(postDialog)}>
                Rate Your Volley Session
            </CloseableDialogTitle>
            <DialogContent>
                <Box display="flex" justifyContent="center" component="div">
                    <FiveStarRating
                        name="simple-controlled"
                        value={rating}
                        onChange={(_, newValue) => setRating(newValue)}
                        sx={{ fontSize: "3rem", mb: 4 }}
                    />
                </Box>
                <TextField
                    id="comment"
                    label="Additional Comments"
                    type="text"
                    fullWidth
                    multiline
                    rows={3}
                    value={comment}
                    onChange={(e) => setComments(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Box display="flex" flexDirection="column" width="100%">
                    <Button
                        onClick={handleSave}
                        color="primary"
                        disabled={rating === null}
                        loading={submitting}
                        fullWidth
                    >
                        Submit
                    </Button>
                    {apiError && (
                        <Typography
                            variant="body2"
                            color="error"
                            sx={{ mt: 1, textAlign: "center" }}
                            role="alert"
                        >
                            {apiError}
                        </Typography>
                    )}
                </Box>
            </DialogActions>
        </>
    );
}

export function DisconnectFeedbackDialog(): React.JSX.Element {
    return <FeedbackDialog postDialog="DisconnectCleanupChecklist" />;
}

export function ShutdownFeedbackDialog(): React.JSX.Element {
    return <FeedbackDialog postDialog="ShutdownCleanupChecklist" />;
}
